import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Reporte from "../../page/reportes/Reporte";
import apiAntesala from "../../../config/ConfigPublic";
import apiPrivateAntesala from "../../../config/ConfigPrivate";

const ReporteController = () => {
  const navigate = useNavigate();

  // Función para generar código aleatorio con fecha
  const generarCodigo = () => {
    const fecha = new Date();
    const year = fecha.getFullYear();
    const month = String(fecha.getMonth() + 1).padStart(2, '0');
    const day = String(fecha.getDate()).padStart(2, '0');
    const hours = String(fecha.getHours()).padStart(2, '0');
    const minutes = String(fecha.getMinutes()).padStart(2, '0');
    const seconds = String(fecha.getSeconds()).padStart(2, '0');
    const random = Math.random().toString(36).substring(2, 8).toUpperCase();

    return `${year}${month}${day}${hours}${minutes}${seconds}-${random}`;
  };

  const [formData, setFormData] = useState({
    usuarios_id: "",
    eventos_id: "",
    entradas_id: "",
    decidir_id: "",
    cantidad: "",
    unidad: "",
    total: "",
    fecha: "",
    codigo: generarCodigo(), // Código generado automáticamente
    codigo_descuento: "",
    descuento: "",
    multiplicador: "",
    nombre_cliente: "",
    dni_cliente: "",
    email_cliente: "",
    telefono_cliente: "",
    ingreso: "ESPERA", // Valor por defecto ESPERA
    status: "ACTIVO",
    comision: "15", // Default 15%
    comisiones_id: "2", // ID de comisión por defecto
    membership_code: "",
  });

  const [validate, setValidate] = useState({
    usuarios_id: { status: false, msg: "" },
    eventos_id: { status: false, msg: "" },
    entradas_id: { status: false, msg: "" },
    cantidad: { status: false, msg: "" },
    unidad: { status: false, msg: "" },
    total: { status: false, msg: "" },
    fecha: { status: false, msg: "" },
    nombre_cliente: { status: false, msg: "" },
    email_cliente: { status: false, msg: "" },
    status: { status: false, msg: "" },
  });

  // Estados para las búsquedas
  const [usuarios, setUsuarios] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [entradas, setEntradas] = useState([]);
  const [fechasDisponibles, setFechasDisponibles] = useState([]);
  const [selectedUsuario, setSelectedUsuario] = useState(null);
  const [selectedEvento, setSelectedEvento] = useState(null);

  // Estados de carga
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const [loadingEventos, setLoadingEventos] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  // Estados de búsqueda
  const [searchUsuario, setSearchUsuario] = useState("");
  const [searchEvento, setSearchEvento] = useState("");

  // Referencias para AbortController
  const abortControllerUsuarios = useRef(null);
  const abortControllerEventos = useRef(null);

  // Buscar usuarios con debounce y AbortController
  const buscarUsuarios = useCallback(async (nombre = "") => {
    // Cancelar la búsqueda anterior si existe
    if (abortControllerUsuarios.current) {
      abortControllerUsuarios.current.abort();
    }

    // Crear un nuevo AbortController
    abortControllerUsuarios.current = new AbortController();

    setLoadingUsuarios(true);
    try {
      const params = new URLSearchParams({
        "sort": "-id",
        "per_page": "10",
      });

      if (nombre) {
        params.append("filter[nombre]", nombre);
      }

      const response = await apiPrivateAntesala.get(`/usuarios?${params.toString()}`, {
        signal: abortControllerUsuarios.current.signal,
      });

      if (response.data && response.data.data) {
        setUsuarios(response.data.data);
      }
    } catch (error) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        // Búsqueda cancelada, no hacer nada
        console.log("Búsqueda de usuarios cancelada");
      } else {
        console.error("Error al buscar usuarios:", error);
        setUsuarios([]);
      }
    } finally {
      setLoadingUsuarios(false);
    }
  }, []);

  // Buscar eventos con debounce y AbortController
  const buscarEventos = useCallback(async (nombre = "") => {
    // Cancelar la búsqueda anterior si existe
    if (abortControllerEventos.current) {
      abortControllerEventos.current.abort();
    }

    // Crear un nuevo AbortController
    abortControllerEventos.current = new AbortController();

    setLoadingEventos(true);
    try {
      const params = new URLSearchParams();

      if (nombre) {
        params.append("filter[nombre]", nombre);
      }

      const response = await apiPrivateAntesala.get(`/eventos?${params.toString()}`, {
        signal: abortControllerEventos.current.signal,
      });

      if (response.data && response.data.data) {
        setEventos(response.data.data);
      }
    } catch (error) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        // Búsqueda cancelada, no hacer nada
        console.log("Búsqueda de eventos cancelada");
      } else {
        console.error("Error al buscar eventos:", error);
        setEventos([]);
      }
    } finally {
      setLoadingEventos(false);
    }
  }, []);

  // Efecto para buscar usuarios cuando cambia el término de búsqueda
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      buscarUsuarios(searchUsuario);
    }, 300);

    return () => {
      clearTimeout(timeoutId);
      // Cancelar la búsqueda al desmontar o al cambiar el término
      if (abortControllerUsuarios.current) {
        abortControllerUsuarios.current.abort();
      }
    };
  }, [searchUsuario, buscarUsuarios]);

  // Efecto para buscar eventos cuando cambia el término de búsqueda
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      buscarEventos(searchEvento);
    }, 300);

    return () => {
      clearTimeout(timeoutId);
      // Cancelar la búsqueda al desmontar o al cambiar el término
      if (abortControllerEventos.current) {
        abortControllerEventos.current.abort();
      }
    };
  }, [searchEvento, buscarEventos]);

  // Efecto para actualizar entradas cuando se selecciona un evento
  useEffect(() => {
    if (selectedEvento && selectedEvento.entradas) {
      setEntradas(selectedEvento.entradas);
    } else {
      setEntradas([]);
    }

    // Parsear fechasJSON si existe
    if (selectedEvento && selectedEvento.fechasJSON) {
      try {
        const fechas = typeof selectedEvento.fechasJSON === 'string'
          ? JSON.parse(selectedEvento.fechasJSON)
          : selectedEvento.fechasJSON;
        setFechasDisponibles(Array.isArray(fechas) ? fechas : []);
      } catch (error) {
        console.error("Error al parsear fechasJSON:", error);
        setFechasDisponibles([]);
      }
    } else {
      setFechasDisponibles([]);
    }
  }, [selectedEvento]);

  // Calcular total cuando cambia la cantidad o precio unitario
  useEffect(() => {
    const cantidad = parseFloat(formData.cantidad) || 0;
    const unidad = parseFloat(formData.unidad) || 0;
    const total = cantidad * unidad;

    if (formData.cantidad && formData.unidad) {
      setFormData(prev => ({
        ...prev,
        total: total.toFixed(2)
      }));
    }
  }, [formData.cantidad, formData.unidad]);

  // Manejar selección de usuario
  const handleSelectUsuario = (usuario) => {
    setSelectedUsuario(usuario);
    setFormData(prev => ({
      ...prev,
      usuarios_id: usuario.id,
      nombre_cliente: usuario.nombre || "",
      email_cliente: usuario.email || "",
      dni_cliente: usuario.dni || "",
      telefono_cliente: usuario.telefono || "",
    }));
    setSearchUsuario(usuario.nombre || `Usuario #${usuario.id}`);
  };

  // Manejar selección de evento
  const handleSelectEvento = (evento) => {
    setSelectedEvento(evento);
    setFormData(prev => ({
      ...prev,
      eventos_id: evento.id,
      entradas_id: "", // Reset entrada cuando cambia el evento
      unidad: "", // Reset precio
      fecha: "", // Reset fecha
    }));
    setSearchEvento(evento.nombre || `Evento #${evento.id}`);
  };

  // Manejar selección de entrada
  const handleSelectEntrada = (e) => {
    const entradaId = e.target.value;
    const entrada = entradas.find(ent => ent.id == entradaId);

    if (entrada) {
      setFormData(prev => ({
        ...prev,
        entradas_id: entrada.id,
        unidad: entrada.precio || "",
        multiplicador: entrada.multiplicador || "", // Auto-rellenar multiplicador
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        entradas_id: "",
        unidad: "",
        multiplicador: "",
      }));
    }
  };

  // Manejar cambios en el formulario
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Validar formulario
  const validarFormulario = () => {
    const errores = {};
    let isValid = true;

    if (!formData.usuarios_id) {
      errores.usuarios_id = { status: true, msg: "Debe seleccionar un usuario" };
      isValid = false;
    }

    if (!formData.eventos_id) {
      errores.eventos_id = { status: true, msg: "Debe seleccionar un evento" };
      isValid = false;
    }

    if (!formData.entradas_id) {
      errores.entradas_id = { status: true, msg: "Debe seleccionar una entrada" };
      isValid = false;
    }

    if (!formData.cantidad || formData.cantidad <= 0) {
      errores.cantidad = { status: true, msg: "La cantidad debe ser mayor a 0" };
      isValid = false;
    }

    if (!formData.unidad || formData.unidad <= 0) {
      errores.unidad = { status: true, msg: "El precio unitario debe ser mayor a 0" };
      isValid = false;
    }

    if (!formData.total || formData.total <= 0) {
      errores.total = { status: true, msg: "El total debe ser mayor a 0" };
      isValid = false;
    }

    if (!formData.fecha) {
      errores.fecha = { status: true, msg: "Debe seleccionar una fecha" };
      isValid = false;
    }

    if (!formData.nombre_cliente) {
      errores.nombre_cliente = { status: true, msg: "El nombre del cliente es requerido" };
      isValid = false;
    }

    if (!formData.email_cliente) {
      errores.email_cliente = { status: true, msg: "El email del cliente es requerido" };
      isValid = false;
    }

    setValidate({
      usuarios_id: errores.usuarios_id || { status: false, msg: "" },
      eventos_id: errores.eventos_id || { status: false, msg: "" },
      entradas_id: errores.entradas_id || { status: false, msg: "" },
      cantidad: errores.cantidad || { status: false, msg: "" },
      unidad: errores.unidad || { status: false, msg: "" },
      total: errores.total || { status: false, msg: "" },
      fecha: errores.fecha || { status: false, msg: "" },
      nombre_cliente: errores.nombre_cliente || { status: false, msg: "" },
      email_cliente: errores.email_cliente || { status: false, msg: "" },
      status: { status: false, msg: "" },
    });

    return isValid;
  };

  // Enviar formulario
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validarFormulario()) {
      console.log("Formulario inválido");
      return;
    }

    setLoadingSubmit(true);

    try {
      // Preparar datos para enviar con comisiones_id en 2
      const dataToSend = {
        ...formData,
        comisiones_id: "2", // Forzar comisiones_id a 2
      };

      const FORMDATA = new FormData();
      FORMDATA.append('user_id', dataToSend.usuarios_id);
      FORMDATA.append('nombre', dataToSend.nombre_cliente);
      FORMDATA.append('email', dataToSend.email_cliente);
      FORMDATA.append('telefono', dataToSend.telefono_cliente);
      FORMDATA.append('dni', dataToSend.dni_cliente);
      FORMDATA.append('evento_id', dataToSend.eventos_id);
      FORMDATA.append('fecha', dataToSend.fecha);

      FORMDATA.append('entradas[0][id]', dataToSend.entradas_id);
      FORMDATA.append('entradas[0][quantity]', dataToSend.cantidad);

      if (dataToSend.cupon) { FORMDATA.append('cupon', dataToSend.cupon) }

      console.log("Enviando reporte:", dataToSend);
      const response = await apiPrivateAntesala.post('/reportes', FORMDATA);

      if (response.data) {
        // Redirigir a la vista de reportes
        navigate('/dashboard/reportes');
      }
    } catch (error) {
      console.error("Error al crear el reporte:", error);
    } finally {
      setLoadingSubmit(false);
    }
  };

  return (
    <Reporte
      formData={formData}
      validate={validate}
      handleChange={handleChange}
      handleSubmit={handleSubmit}
      loadingSubmit={loadingSubmit}
      // Usuarios
      usuarios={usuarios}
      searchUsuario={searchUsuario}
      setSearchUsuario={setSearchUsuario}
      handleSelectUsuario={handleSelectUsuario}
      loadingUsuarios={loadingUsuarios}
      selectedUsuario={selectedUsuario}
      // Eventos
      eventos={eventos}
      searchEvento={searchEvento}
      setSearchEvento={setSearchEvento}
      handleSelectEvento={handleSelectEvento}
      loadingEventos={loadingEventos}
      selectedEvento={selectedEvento}
      // Entradas
      entradas={entradas}
      handleSelectEntrada={handleSelectEntrada}
      // Fechas
      fechasDisponibles={fechasDisponibles}
    />
  );
};

export default ReporteController;
